import { useQueryClient } from '@tanstack/react-query'
import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { ConsentReconfirmModal } from '#components/ConsentReconfirmModal'
import { useAuth } from '#hooks/useAuth'
import { useUserNav } from '#hooks/useUserNav'
import { useFetchProfile } from '#pages/Profile/hooks/useFetchProfile'
import { useUpdateProfile } from '#pages/Profile/hooks/useUpdateProfile'

interface IProps {
  children: React.ReactNode
}

/**
 * Blocks the ROLE_USER self-view with a mandatory terms re-confirmation modal
 * (decision B-12) for users who consented BEFORE the audit trail existed —
 * `conditionsAgreed === true && conditionsAgreedAt === null`. Rendered inside
 * `UserNavProvider` so `useFetchProfile` resolves the current user.
 *
 * Behaviour:
 * - while the profile loads → render children and keep the modal closed. The
 *   page shows its own block skeletons. A page spinner here would cover them.
 * - legacy consent → render children + the blocking modal on top
 * - any other state (up-to-date consent, non-legacy, fetch error) → render
 *   children. On a fetch error we deliberately fail OPEN: a failed profile call
 *   must never lock a user out of the app.
 *
 * Confirming sends an explicit `{ conditionsAgreed: true }` PATCH (never the
 * ProfileEditForm's `toApiPayload`, which omits the flag when unchanged). On
 * success it flips a local `hasConfirmed` flag that closes the gate immediately —
 * the profile query is still invalidated for freshness, but the gate never
 * depends on the refetched `conditionsAgreedAt` to close, so an eventually-
 * consistent backend can't strand the user behind the modal after a success toast.
 */
export const ConsentReconfirmGate = ({ children }: IProps): React.ReactNode => {
  // 1. State
  const [isLoggingOut, setIsLoggingOut] = useState<boolean>(false)
  // Closes the gate the moment the PATCH succeeds, independent of when (or
  // whether) the refetched profile reflects the new `conditionsAgreedAt`.
  const [hasConfirmed, setHasConfirmed] = useState<boolean>(false)

  // 2. Hooks
  const { t } = useTranslation()
  const { logout } = useAuth()
  const userNav = useUserNav()
  const queryClient = useQueryClient()
  const { profile, isLoading, isError } = useFetchProfile()
  const { mutateAsync, isPending } = useUpdateProfile()

  // 3. Derived state
  const isLegacyConsent = profile?.conditionsAgreed === true && profile.conditionsAgreedAt === null

  // 4. Callbacks
  const handleConfirm = useCallback(async (): Promise<void> => {
    if (!userNav) return

    try {
      await mutateAsync({
        params: { path: { companyId: userNav.companyId, userId: userNav.userId } },
        // Explicit minimal payload: re-affirming consent is the entire point, so we
        // always send `true` (unlike toApiPayload, which omits it when unchanged).
        body: { conditionsAgreed: true },
        headers: { 'Content-Type': 'application/merge-patch+json' },
      })

      // Close the gate now, from local state — never wait on the refetch.
      setHasConfirmed(true)

      // Still invalidate so the profile is fresh (e.g. `conditionsAgreedAt` shows
      // up elsewhere), but the gate no longer relies on this to close.
      await queryClient.invalidateQueries({ queryKey: ['get', '/companies/{companyId}/users/{id}'] })
      toast.success(t('Thank you for confirming the terms and conditions'))
    } catch {
      toast.error(t('Something went wrong while saving. Please try again.'))
    }
  }, [mutateAsync, queryClient, t, userNav])

  const handleLogout = useCallback(async (): Promise<void> => {
    setIsLoggingOut(true)
    try {
      await logout()
    } catch {
      // Logout failed — re-enable the button so the user can retry.
      setIsLoggingOut(false)
    }
  }, [logout])

  // 6. Guard clauses
  // While the profile is in flight it is undefined, so this is not legacy consent
  // and the modal stays closed. Children render so the dashboard can show block
  // skeletons instead of a page spinner (UE-19). A resolved legacy profile still
  // opens the modal below.
  if (isLoading || isError || !isLegacyConsent || hasConfirmed) return children

  // 7. Render
  return (
    <>
      {children}
      <ConsentReconfirmModal
        onConfirm={handleConfirm}
        onLogout={handleLogout}
        isConfirming={isPending}
        isLoggingOut={isLoggingOut}
      />
    </>
  )
}
